import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { getModernTableStyles } from "../Tables/modernTableStyles"
import { Row, Col, CardHeader, CardBody, Spinner, Button } from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { toast } from "react-toastify"
import { fetchSubscriptionHistory } from "../../store/subscription/actions"
import { getRenewalInfo } from "../../api/requests/subscription-requests"
import { downloadSubscriptionInvoicePdf } from "../../utils/subscriptionInvoicePdf"
import { formatLicenseDate } from "../../utils/subscriptionDates"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"

const typeMap = {
  PLAN: { label: "Paket", color: "#221C5D" },
  MODULE: { label: "Modül", color: "#E56A22" },
}

const statusMap = {
  ACTIVE: { label: "Aktif", color: "#71C30B" },
  EXPIRED: { label: "Sona Erdi", color: "#B0B7C3" },
  TRIAL: { label: "Deneme", color: "#F2C96D" },
  PENDING: { label: "Ödeme Bekliyor", color: "#E56A22" },
  CANCELLED: { label: "İptal", color: "#E07A7A" },
  SCHEDULED: { label: "Planlandı", color: NAVY },
}

const SubscriptionHistory = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { history, loading } = useSelector((state) => state.subscription)
  const [downloadingId, setDownloadingId] = useState(null)
  const [renewal, setRenewal] = useState(null)
  const [renewalLoading, setRenewalLoading] = useState(true)


  /* Tablo ve buton görünümü liste ekranlarının ortak kaynağından geliyor; "Rapor Oluştur"
     butonuyla aynı sınıflar (btn-rounded + btn-navy-outline) burada da geçerli olsun diye. */
  const styles = `
    ${getModernTableStyles("history-modern")}
    .history-modern .code-dark { color: #000 !important; }
    @media (max-width: 576px) {
      .history-modern .table { font-size: 12px; }
      .history-modern .table td, .history-modern .table th { padding: .5rem; }
    }
  `

  document.title = "Satın Alma Geçmişi | Tahsilat360"

  useEffect(() => {
    dispatch(fetchSubscriptionHistory())
  }, [dispatch])

  // Yenileme bilgisi geçmişten ayrı bir uçtan gelir: geçmiş satırları ödenmiş tutarı
  // dondurur, bu ise henüz gerçekleşmemiş yenilemenin bugünkü fiyatıdır.
  useEffect(() => {
    let active = true
    getRenewalInfo()
      .then((json) => { if (active) setRenewal(json?.data ?? null) })
      .catch(() => { if (active) setRenewal(null) })
      .finally(() => { if (active) setRenewalLoading(false) })
    return () => { active = false }
  }, [])

  const handleDownloadInvoice = async (item) => {
    try {
      setDownloadingId(item.id)
      await downloadSubscriptionInvoicePdf(item)
    } catch (err) {
      toast.error(err.message || "Fatura indirilemedi", { position: "top-right", autoClose: 3000 })
    } finally {
      setDownloadingId(null)
    }
  }

  const formatDate = (d) => formatLicenseDate(d)

  const formatPrice = (price) => {
    if (price == null) return "-"
    return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + " ₺"
  }

  const renderType = (type) => {
    const t = typeMap[type] || typeMap.PLAN
    return (
      <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: t.color, color: "#fff" }}>
        {t.label}
      </span>
    )
  }

  const renderStatus = (status) => {
    const s = statusMap[status] || { label: status, color: "#B0B7C3" }
    return (
      <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: s.color, color: "#fff" }}>
        {s.label}
      </span>
    )
  }

  return (
    <div className="page-content history-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        {!renewalLoading && renewal?.hasActiveSubscription && (
          <Row className="mb-3">
            <Col>
              <TableCard>
                <CardBody className="d-flex flex-wrap gap-4 align-items-center justify-content-between">
                  <div>
                    <div className="text-muted" style={{ fontSize: ".8rem" }}>SONRAKİ YENİLEME</div>
                    <div className="fw-bold text-dark">{formatDate(renewal.nextRenewalDate)}</div>
                    <div className="text-muted" style={{ fontSize: ".85rem" }}>{renewal.planName}</div>
                  </div>

                  <div>
                    <div className="text-muted" style={{ fontSize: ".8rem" }}>YENİLEME FİYATI</div>
                    <div className="fw-bold" style={{ color: NAVY }}>{formatPrice(renewal.renewalPrice)}</div>
                    <div className="text-muted" style={{ fontSize: ".85rem" }}>
                      {renewal.hasPriceOverride ? (
                        <>
                          <span className="badge border-0 me-1" style={{ borderRadius: 999, backgroundColor: "#71C30B", color: "#fff" }}>
                            Size özel
                          </span>
                          liste fiyatı {formatPrice(renewal.basePrice)}
                        </>
                      ) : (
                        <>KDV dahil{renewal.vatAmount != null ? ` (${formatPrice(renewal.vatAmount)} KDV)` : ""}</>
                      )}
                    </div>
                  </div>

                  {/* Otomatik yenileme GÖSTERİLMİYOR: auto_renew kolonu her zaman false
                      yazılıyor, açan bir uç ve yenileyen bir job yok. Yerine kullanıcıyı
                      geçebileceği paketlere götüren buton konuldu. */}
                  <div>
                    <Button
                      onClick={() => navigate("/subscription/plans")}
                      className="d-flex align-items-center gap-1 btn-rounded btn-navy-outline"
                      title="Geçebileceğiniz paketleri görüntüler"
                    >
                      <i className="bx bx-package" />{" "}Paketler
                    </Button>
                    <div className="text-muted mt-1" style={{ fontSize: ".85rem" }}>
                      Geçebileceğiniz paketleri görün
                    </div>
                  </div>
                </CardBody>
              </TableCard>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <TableCard>
              <CardHeader data-tour="subscription-history" className="bg-white border-0 rounded-top-4">
                <div className="d-none d-md-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="mb-1 fw-bold text-dark">Satın Alma Geçmişi</h5>
                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                      {history.length > 0 ? `Toplam ${history.length} kayıt` : "Kayıt bulunamadı"}
                    </p>
                  </div>
                </div>
                <div className="d-md-none">
                  <h5 className="mb-1 fw-semibold">Satın Alma Geçmişi</h5>
                  <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                    {history.length > 0 ? `${history.length} kayıt` : "Kayıt yok"}
                  </p>
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {loading ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Geçmiş yükleniyor...</h6>
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-history display-4 d-block mb-2 text-muted" />
                    <p className="text-muted mb-0">Henüz bir satın alma kaydınız bulunmuyor.</p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="text-muted" style={{ fontSize: ".9rem" }}>
                        Toplam <strong>{history.length}</strong> kayıt
                      </div>
                    </div>

                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Tür</Th>
                            <Th>Ürün</Th>
                            <Th>Başlangıç</Th>
                            <Th>Bitiş</Th>
                            <Th>Tutar</Th>
                            <Th className="d-none d-md-table-cell">Kupon</Th>
                            <Th>Durum</Th>
                            <Th className="text-center">İşlem</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {history.map((item, idx) => (
                            <Tr key={item.id || idx} className="border-bottom">
                              <Td>{renderType(item.type)}</Td>
                              <Td className="fw-semibold text-dark">
                                {item.planName}
                                {item.renewedFromId && (
                                  <span className="text-muted d-block" style={{ fontSize: ".8rem" }}>Yenileme</span>
                                )}
                              </Td>
                              <Td>{formatDate(item.startDate)}</Td>
                              <Td>{formatDate(item.endDate)}</Td>
                              <Td>
                                {formatPrice(item.paidAmount)}
                                {item.vatAmount > 0 && (
                                  <span className="text-muted d-block" style={{ fontSize: ".8rem" }}>
                                    {formatPrice(item.vatAmount)} KDV dahil
                                  </span>
                                )}
                              </Td>
                              <Td className="d-none d-md-table-cell">
                                {item.couponCode ? (
                                  <>
                                    <code className="code-dark fw-bold">{item.couponCode}</code>
                                    {item.couponDiscount > 0 && (
                                      <span className="text-muted d-block" style={{ fontSize: ".8rem" }}>
                                        -{formatPrice(item.couponDiscount)}
                                      </span>
                                    )}
                                  </>
                                ) : "-"}
                              </Td>
                              <Td>{renderStatus(item.status)}</Td>
                              <Td className="text-center">
                                <div className="d-flex align-items-center justify-content-center gap-1 flex-wrap">
                                  {item.paidAmount > 0 && (
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="btn-rounded"
                                      style={{ borderRadius: 999 }}
                                      disabled={downloadingId === item.id}
                                      onClick={() => handleDownloadInvoice(item)}
                                      title="Fatura İndir"
                                    >
                                      {downloadingId === item.id ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <i className="bx bx-download" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </div>

                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

    </div>
  )
}

export default SubscriptionHistory
